import type { ReactNode, Ref } from "react";
import { ChevronRight } from "lucide-react";

import { Select, SelectContent, SelectTrigger } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/engine/support/classNames";

/**
 * One row of a settings card: icon, label, and whatever ends it. One class for
 * every kind of row, so a switch, a list and a link read as the same thing.
 */
const ROW =
  "flex w-full min-w-0 min-h-11 items-center gap-3 px-3 py-2 text-left text-sm font-medium text-foreground transition-colors duration-(--motion-quick) focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";

/** The icon slot every row shares, so labels start on one line. */
export function SettingsRowIcon({ children }: { children: ReactNode }) {
  return (
    <span
      aria-hidden="true"
      className="flex size-4 shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-4"
    >
      {children}
    </span>
  );
}

/** A small heading over a card of rows, divided by hairlines. */
export function SettingsGroup({
  title,
  children,
  className,
  ...rest
}: {
  title?: ReactNode;
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}) {
  return (
    <section
      className={cn("flex min-w-0 flex-col gap-1.5", className)}
      {...rest}
    >
      {title && (
        <h3 className="flex items-center gap-1.5 px-3 text-xs font-semibold text-muted-foreground">
          {title}
        </h3>
      )}
      <div className="flex min-w-0 flex-col divide-y divide-border overflow-hidden rounded-xl border border-border bg-raise">
        {children}
      </div>
    </section>
  );
}

/**
 * A row whose whole width opens a list: icon, label, and what is chosen. The
 * list's items are the children.
 */
export function SettingsSelectRow({
  icon,
  label,
  chosen,
  chosenMark,
  value,
  onValueChange,
  "aria-label": ariaLabel,
  "data-testid": testId,
  menuAction = false,
  ref,
  children,
}: {
  icon: ReactNode;
  label: ReactNode;
  /** The row itself, for a caller that hands focus back to it. */
  ref?: Ref<HTMLButtonElement>;
  /** What the row says is chosen, beside the label. */
  chosen: ReactNode;
  /** Drawn ahead of what is chosen: a palette's colours, say. */
  chosenMark?: ReactNode;
  value: string;
  onValueChange: (value: string) => void;
  "aria-label": string;
  "data-testid": string;
  /** Walked by the arrow keys of the menu it stands in. */
  menuAction?: boolean;
  children: ReactNode;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        ref={ref}
        data-menu-action={menuAction || undefined}
        data-testid={testId}
        aria-label={ariaLabel}
        className={cn(
          ROW,
          "h-auto rounded-none border-0 bg-transparent shadow-none ring-offset-0 hover:bg-muted focus:ring-0 [&>span]:line-clamp-none [&>svg:last-child]:text-muted-foreground",
        )}
      >
        <SettingsRowIcon>{icon}</SettingsRowIcon>
        <span className="flex-1">{label}</span>
        {/* A div: the trigger clamps its span children into boxes, which
            would stack a swatch over the name beside it. */}
        <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
          {chosenMark}
          <span className="truncate">{chosen}</span>
        </div>
      </SelectTrigger>
      {/* The list is portalled, but React bubbles its keys through the portal
          to the panel around the row, which closes on Escape: an Escape meant
          for the list would close the settings with it. */}
      <SelectContent onKeyDown={(event) => event.stopPropagation()}>
        {children}
      </SelectContent>
    </Select>
  );
}

/** A row that is one switch: the whole row is its label. */
export function SettingsSwitchRow({
  icon,
  label,
  note,
  checked,
  onCheckedChange,
  "data-testid": testId,
  id,
  switchTestId,
  menuAction = false,
}: {
  icon?: ReactNode;
  label: ReactNode;
  /** A second line under the label, saying what the switch does. */
  note?: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  "data-testid"?: string;
  /** The switch's own id, for whoever addresses it from outside. */
  id?: string;
  switchTestId?: string;
  /** Walked by the arrow keys of the menu it stands in. */
  menuAction?: boolean;
}) {
  return (
    <label
      data-testid={testId}
      className={cn(ROW, "cursor-pointer hover:bg-muted")}
    >
      {icon && <SettingsRowIcon>{icon}</SettingsRowIcon>}
      <span className="min-w-0 flex-1">
        {label}
        {note && (
          <span className="block text-xs font-normal text-muted-foreground">
            {note}
          </span>
        )}
      </span>
      <Switch
        id={id}
        data-menu-action={menuAction || undefined}
        data-testid={switchTestId}
        checked={checked}
        onCheckedChange={onCheckedChange}
      />
    </label>
  );
}

/** A row that opens something else. */
export function SettingsLinkRow({
  icon,
  label,
  onClick,
  "data-testid": testId,
}: {
  icon: ReactNode;
  label: ReactNode;
  onClick: () => void;
  "data-testid"?: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={cn(ROW, "hover:bg-muted")}
    >
      <SettingsRowIcon>{icon}</SettingsRowIcon>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <ChevronRight
        className="size-4 flex-none text-muted-foreground"
        aria-hidden
      />
    </button>
  );
}
